import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { Shelf, Spinner, WarningMessage } from 'components/widgets';
import { FieldGroup, RunReportButton } from 'containers/widgets';
import { GeneralPopup, Grid, LineChart, PageHeader, PageWrapper, PopupTypes } from 'components/partials';
import ReportsNoData from 'components/partials/ReportsNoData';
import Checkbox from '../atoms/Checkbox';
import css from './Reports.css';
import fieldGroupCss from '../molecules/FieldGroup.css';
import {
    ANALYTICS_EVENT_PREFIX,
    REPORTS_EVENT_PREFIX,
    trackMixpanelEvent,
    trackShowVideoMixpanelEvent,
} from '../../utils/general';
import PivotChart from '../partials/PivotChart';
import GlassesIcon from '../../resources/svg/glasses.svg';
import ChartIcon from '../../resources/svg/chart.svg';
import ChevronIcon from '../../resources/icons/chevron.svg';
import { AVAILABILITY_PARAMETER_OPTION__DIMENSION } from '../../fieldsAvailability/consts';
import EditShelf from './reports/EditShelf';
import ReportsEmptyState from './reports/emptyStates/ReportsEmptyState';
import { PAGE_WRAPPER_NO_PADDING_STYLE } from '../../utils/consts';
import FiltersSector from '../partials/FiltersSector';
import { defaultFilters, isPivotMode, isPivotReport, reportTypes } from '../../utils/reports';
import Banner from '../utilities/Banner';
import ReportBannerIcon from '../../resources/svg/reportBannerIcon.svg';
import { insertUserBannerCloseClicks, shouldDisplayBanner } from './reports/utils';
import User from '../../reducers/user';
import { getCollisionWarningParams, REPORT_TYPES_WITH_COLLISIONS } from '../../utils/dimensionsCollisions';
import FieldsAvailability from '../../fieldsAvailability/components/fieldsAvailability';
import Icon from '../foundations/Icon';
import { FIELD_SECTION, FIELD_SECTION_DATA, NEW_SECTIONS } from './reports/consts';
import { CreativeExploreEmptyState } from './reports/emptyStates/CreativeExploreEmptyState';
import SearchTextField from '../molecules/SearchTextField/SearchTextField';
import AdvancedModeButton from '../AdvancedModeButton';
import ClearFieldsButton from './reports/ClearFieldsButton';
import Tag from '../atoms/Tag';
import { ADWORDS_DISPLAY_NAME } from '../../utils/reportsConsts';

let hoverStartTime = null;

const TRANSITION_CONFIG = {
    classNames: {
        enter: css.smoothOpenAnimationEnter,
        enterActive: css.smoothOpenAnimationEnterActive,
        exit: css.smoothOpenAnimationLeave,
        exitActive: css.smoothOpenAnimationLeaveActive,
    },
    timeout: { enter: 400, exit: 50 },
};

const CREATIVE_INTERPOLATION_WARNING = {
    FIELDS: ["creative_id", "creative_name", "asset_id", "asset_name"],
    SOURCES: [ADWORDS_DISPLAY_NAME],
}

const onUnifiedBannerRedirectButtonClicked = () => {
    trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Unified banner redirect button had been clicked', {});
    window.location.href = '#/react/unified_report';
};

const onUnifiedBannerCloseButtonClicked = () => {
    trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Unified banner close button had been clicked', {});
    insertUserBannerCloseClicks();
};

const Reports = props => {
    const [expandedSections, setExpandedSections] = useState({});
    const [search, setSearch] = useState('');

    const prevTableShowRef = useRef();
    const prevReportTypeRef = useRef();

    const {
        shelf = [],
        header = {},
        showSpinner = false,
        chart = { enabled: false, show: false },
        table = { enabled: false, show: false },
        chartData = {},
        reportData = {},
        reportType = 'reports',
        onToggleChart = () => {},
        showReportMeta = false,
        showHeader,
        validationError = '',
        showPivotCharts,
        activeBookmarkName,
        openFieldsAvailability,
        fieldsAvailabilityFieldName,
        onFieldsAvailabilityClick = () => {},
        showFieldsAvailabilityFeature = false,
        adminMode = false,
        showPopupDialog = false,
        popupDialogParams = {},
        emptyStateOrgData = {},
        onCloseReportsEmptyState = () => {},
        isEditShelfOpen = false,
        showFieldsSelection = false,
        isShelfReporting = false,
        reportFilters = [],
        permissionKeys = [],
        fieldGroupsToHide = {},
        isSlimMode,
        selectedApps,
        onFiltersExpand = () => {},
        onSearchFilterChanged = () => {},
        onUpdateChartData = () => {},
        onFieldSet = () => {},
        onFieldClick = () => {},
        onChartLinesChanged = () => {},
        onChartLegendToggle = () => {},
        onChartMetricSelected = () => {},
        onChartLoaded = () => {},
        onChartTimeBreakdownChanged = () => {},
        onPivotChartChange = () => {},
        onUpdateQueryURL = () => {},
        changePopupDialog = null,
        customFieldGroupLabels = {},
        selectedSources = [],
        selectedFields = {},
        reportWarning = {},
        user,
        translate,
    } = props;

    const isCreativeExploreReport = reportType === reportTypes.creativeExplore;
    const isUnifiedReport = reportType === reportTypes.unifiedReport;

    useEffect(() => {
        setSearch('');
    }, [showFieldsSelection]);

    useEffect(() => {
        const currentTableShow = table.show;

        if (currentTableShow !== prevTableShowRef.current) {
            if (currentTableShow && !isCreativeExploreReport) {
                onFiltersExpand(false);
            }
        }

        if (reportType !== prevReportTypeRef.current) {
            setSearch('');
        }

        prevTableShowRef.current = currentTableShow;
        prevReportTypeRef.current = reportType;
    }, [table.show, reportType, onFiltersExpand]);

    const toggleSectionExpansion = useCallback((section, isExpended) => {
        setExpandedSections(prevState => ({
            ...prevState,
            [section]: !isExpended,
        }));
    }, []);

    const isSectionExpanded = useCallback(section => expandedSections[section] !== false, [expandedSections]);

    const updateSearchText = useCallback(
        value => {
            const isCurrentSearchActive = !!value.length;
            const isPreviousSearchActive = !!search.length;

            setSearch(value);

            // If the state of the search has changed
            if (isCurrentSearchActive ^ isPreviousSearchActive) {
                if (isCurrentSearchActive) {
                    window.mixpanel.track(`${ANALYTICS_EVENT_PREFIX}: Search field`, { reportType });
                }

                onSearchFilterChanged(isCurrentSearchActive);
            }
        },
        [search, reportType, onSearchFilterChanged]
    );

    // generates the jsx based on the appropriate reportConfig
    const _getComponent = useCallback(
        (item, index, fields) => {
            const {
                label,
                type,
                icon,
                addButton,
                group,
                field,
                bigLabel,
                bigLabelTooltip,
                checkbox,
                renderChildrenOnly,
            } = item;

            if (type === 'divider') {
                return <div className={css.divider} />;
            }

            if (type === 'dividerBorder') {
                return <div className={fieldGroupCss.borderDivider} />;
            }

            if (type === 'run') {
                return (
                    <div className={css.fieldGroupHolder}>
                        <RunReportButton reportType={reportType} />
                    </div>
                );
            }

            if (type === 'checkbox') {
                const currValue = props[field];
                return (
                    <FieldGroup
                        label={bigLabel}
                        labelTooltip={bigLabelTooltip}
                        reportType={reportType}
                        renderChildrenOnly={renderChildrenOnly}
                        type={field}
                        icon={icon}
                    >
                        <Checkbox
                            style={{ marginBottom: 0, marginTop: 5 }}
                            checked={currValue}
                            onChange={() => onFieldSet(field, !currValue)}
                            label={label}
                        />
                    </FieldGroup>
                );
            }

            const collisionWarningParams =
                REPORT_TYPES_WITH_COLLISIONS.includes(reportType) && type === 'dimensions'
                    ? getCollisionWarningParams(props)
                    : null;
            const collisionWarningMessage = collisionWarningParams
                ? 'STATIC.PAGES.REPORTS.WARNING_DIMENSIONS_COLLISION'
                : null;

            return (
                <FieldGroup
                    bottomComponent={
                        shouldDisplayBanner(index, user, reportType) ? (
                            <Banner
                                buttonOnClick={onUnifiedBannerRedirectButtonClicked}
                                leftIcon={<ReportBannerIcon />}
                                buttonText="STATIC.PAGES.REPORTS.UNIFIED_BANNER.BUTTON"
                                label="STATIC.PAGES.REPORTS.UNIFIED_BANNER.HEADER"
                                subLabel="STATIC.PAGES.REPORTS.UNIFIED_BANNER.SUB_HEADER"
                                className={css.unifiedBanner}
                                onClose={onUnifiedBannerCloseButtonClicked}
                            />
                        ) : null
                    }
                    icon={icon}
                    label={customFieldGroupLabels[type] || label}
                    labelTooltip={bigLabelTooltip}
                    fields={fields}
                    selectedSources={selectedSources}
                    dataTypeFields={selectedFields.dataTypeFields}
                    fieldGroupWarning={group.data?.shouldShowFieldGroupWarning && reportWarning.text}
                    fieldGroupError={collisionWarningMessage}
                    fieldGroupErrorParameters={collisionWarningParams}
                    fieldGroupWarningParameters={reportWarning.params}
                    onFieldClick={(_field, value) => {
                        onFieldClick(_field, value);
                        if (checkbox && _field.name !== checkbox.visibleOnParentValue) {
                            onFieldSet(checkbox.field, false);
                        }
                    }}
                    showFieldsAvailabilityFeature={showFieldsAvailabilityFeature}
                    onFieldsAvailabilityClick={onFieldsAvailabilityClick}
                    renderChildrenOnly={renderChildrenOnly}
                    addButton={addButton}
                    type={type}
                    group={group}
                    reportType={reportType}
                >
                    {checkbox &&
                        props[type].find(_field => {
                            return _field.checked && _field.name === checkbox.visibleOnParentValue;
                        }) && (
                            <Checkbox
                                style={{ marginLeft: 50 }}
                                checked={props[checkbox.field]}
                                onChange={() => onFieldSet(checkbox.field, !props[checkbox.field])}
                                label={checkbox.label}
                            />
                        )}
                </FieldGroup>
            );
        },
        [
            reportType,
            onFieldSet,
            onFieldClick,
            onFieldsAvailabilityClick,
            showFieldsAvailabilityFeature,
            customFieldGroupLabels,
            selectedSources,
            selectedFields.dataTypeFields,
            reportWarning.text,
            reportWarning.params,
            user,
            props,
        ]
    );

    const _getMigrationDatesMessage = useCallback(reportData => {
        // Info message about TI and DPOU migration dates (admin-only)
        const messages = [];

        if (reportData.tiAttributionMigrationDate) {
            messages.push(`TI Migration Date: ${reportData.tiAttributionMigrationDate}`);
        }
        if (reportData.dpouMigrationDates && Object.keys(reportData.dpouMigrationDates).length > 0) {
            messages.push(
                `DPOU Migration Dates: ${Object.entries(reportData.dpouMigrationDates)
                    .map(([logType, migDate]) => `${logType}=${migDate}`)
                    .join(', ')}`
            );
        }
        if (reportData.snowflakeMigrationTime) {
            messages.push(`Snowflake Migration Time (UTC): ${reportData.snowflakeMigrationTime}`);
        }

        return messages.join('<br>');
    }, []);

    const showFields = showFieldsSelection && !isShelfReporting;
    const timeBreakdown = (chart.request && chart.request.time_breakdown[0]) || '';
    const pageWrapperStyle =
        !isShelfReporting && validationError ? { paddingTop: '35px' } : PAGE_WRAPPER_NO_PADDING_STYLE;

    const tiDpouMigrationDatesMessage = _getMigrationDatesMessage(reportData);
    const shouldShowEmptyState = showFieldsSelection && isShelfReporting;

    const onVideoClick = useCallback(() => {
        trackShowVideoMixpanelEvent({ watchedFrom: 'Reports' });
        changePopupDialog(true, {
            videoLink: 'https://vimeo.com/showcase/8962880/embed?autoplay=1',
            type: PopupTypes.VIDEO,
        });
    }, [changePopupDialog]);

    const onPopupDialogClosed = useCallback(() => changePopupDialog(false, {}), [changePopupDialog]);

    const closeFieldsAvailabilityShelf = useCallback(() => onFieldsAvailabilityClick('', false), [
        onFieldsAvailabilityClick,
    ]);

    const defaultFiltersNames = defaultFilters(reportType).map(({ field }) => field);

    const isWarning = validationError && !isShelfReporting;
    const isChartEnabled = chart.enabled && !table.request?.compare;

    const showCreativeExploreEmptyState =
        isCreativeExploreReport &&
        !table.show &&
        !showSpinner &&
        !reportFilters.filter(({ field }) => defaultFiltersNames.includes(field)).every(({ tags }) => tags.length);

    const isEmptyStateMode = showCreativeExploreEmptyState || shouldShowEmptyState;

    const groupedSections = showCreativeExploreEmptyState
        ? {}
        : shelf
              .filter(item => {
                  const { permissionKey, isShowAdmin, type, group, addButton } = item;

                  const hasNoPermission =
                      permissionKey && !permissionKeys.includes(permissionKey) && (!adminMode || !isShowAdmin);

                  if (type === 'checkbox') return !hasNoPermission;

                  const isHiddenByFieldGroup = fieldGroupsToHide[type];

                  const isEmptyNonFilterGroup =
                      group?.type !== 'filter' &&
                      (!props[type] || (!props[type].length && (!addButton || !addButton.show || isSlimMode)));

                  return !(hasNoPermission || isHiddenByFieldGroup || isEmptyNonFilterGroup);
              })
              .reduce((acc, configItem, index) => {
                  const { section = FIELD_SECTION.OTHER, type, label } = configItem;
                  if (!acc[section]) acc[section] = [];
                  const lowerCasedSearch = search?.toLowerCase() || '';
                  let showFullItem = !lowerCasedSearch || section.toLowerCase().includes(lowerCasedSearch);
                  if (!showFullItem) {
                      try {
                          showFullItem =
                              showFullItem ||
                              translate(label)
                                  .toLowerCase()
                                  .includes(lowerCasedSearch);
                      } catch {}
                  }

                  if (type === 'checkbox') {
                      acc[section].push({ configItem, index });
                      return acc;
                  }

                  // If the whole section or subsections passes the search, we will show all the fields under it
                  // Otherwise we will show only the fields that pass the search
                  let fields = props[type] || [];

                  if (!showFullItem) {
                      fields = fields.filter(field => field.display_name.toLowerCase().includes(lowerCasedSearch));
                  }

                  if (section === FIELD_SECTION.AI_TAGS) {
                      fields = fields.filter(({ apps }) => !apps?.length || apps.some(app => selectedApps.includes(app)));
                  }

                  const showAddButton = configItem.addButton?.show && !lowerCasedSearch;
                  if (fields?.length || showAddButton) {
                      acc[section].push({
                          configItem,
                          index,
                          fields: fields.filter(({ display_name: displayName }) => displayName),
                      });
                  }

                  return acc;
              }, {});

    const hasNoResults = !Object.values(groupedSections).filter(items => items.length).length;
    const shouldShowNoData = !showSpinner && table.show && !reportData.rowData.length;

    const pivotTable = table?.pivot_table || table?.request?.pivot_table;
    const pinnedColumns = table?.pin_columns || table?.request?.pin_columns;

    const handleToggleChart = () => {
        const newChartShow = !chart.show;
        onToggleChart(newChartShow);
        const isChartData = !!chartData?.metrics?.length;

        if (newChartShow && !isChartData) {
            onUpdateChartData();
        }

        trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Toggle chart display', {
            hide: chart.show,
        });
    };

    const shouldShowCreativeInterpolationWarning = (section) => {
        if (section !== FIELD_SECTION.COHORT_DATA) return false;

        if (selectedSources.length &&
            !CREATIVE_INTERPOLATION_WARNING.SOURCES.some(source => selectedSources.includes(source))) return false;

        const creativeMeasurementDimensions = selectedFields.creativeMeasurementDimensions || [];

        return creativeMeasurementDimensions.some(
            dim => CREATIVE_INTERPOLATION_WARNING.FIELDS.includes(dim)
        );
    }

    return (
        <>
            {isWarning ? (
                <WarningMessage
                    containerStyle={{
                        width: '100%',
                        position: 'fixed',
                        zIndex: 'calc(var(--stickyZIndex) + 3)',
                    }}
                    type="error"
                    showIcon={false}
                    message={validationError}
                />
            ) : null}
            {showPopupDialog && (
                <GeneralPopup
                    {...popupDialogParams}
                    open={showPopupDialog}
                    type={popupDialogParams.type}
                    onReject={onPopupDialogClosed}
                />
            )}
            <PageWrapper style={pageWrapperStyle} className={css.pageWrapper} disablePadding>
                {showHeader && (
                    <PageHeader
                        {...header}
                        stickyShadow={reportType !== reportTypes.creativeExplore}
                        additionalText={activeBookmarkName}
                        onVideoClick={!header.subText?.hideVideo && onVideoClick}
                        key={reportType}
                        sticky
                        emptyStateOrgData={emptyStateOrgData}
                        onCloseEmptyState={onCloseReportsEmptyState}
                        showEmptyState={emptyStateOrgData.show_empty_state_reports}
                        reportFormHasChanged
                        shouldAddPadding
                        stickyStyle={{
                            paddingTop: isWarning
                                ? '35px'
                                : isCreativeExploreReport
                                ? '10px'
                                : '20px',
                            display: isCreativeExploreReport ? 'flex' : '',
                            alignItems: isCreativeExploreReport ? 'center' : '',
                        }}
                    >
                        {!isShelfReporting && (
                            <FiltersSector reportType={reportType} isEmptyStateMode={isEmptyStateMode} />
                        )}
                    </PageHeader>
                )}
                {showFields &&
                    (showCreativeExploreEmptyState ? (
                        <CreativeExploreEmptyState />
                    ) : (
                        <div className={css.shelfContainer}>
                            {!isUnifiedReport && (
                                <div className={css.topActionsContainer}>
                                    <div className={css.actions}>
                                        <SearchTextField
                                            placeholder="STATIC.PAGES.REPORTS.SEARCH_FIELDS_PLACEHOLDER"
                                            search={search}
                                            onSearchChange={updateSearchText}
                                            className={css.searchTextField}
                                            clearable
                                        />
                                        <ClearFieldsButton reportType={reportType} />
                                    </div>
                                    <AdvancedModeButton reportType={reportType} />
                                </div>
                            )}
                            {hasNoResults ? (
                                <div className={css.noResults}>
                                    <Icon name="mood-sad-squint" />
                                    <Translate id="STATIC.PAGES.REPORTS.NO_RESULTS_FOUND" />
                                </div>
                            ) : (
                                Object.entries(groupedSections).map(([section, items]) => {
                                    const { label, icon, style } = FIELD_SECTION_DATA[section] || {};
                                    const isExpanded = isSectionExpanded(section) || !!search.length;
                                    const isTimeBreakdown = section === FIELD_SECTION.TIME_BREAKDOWNS;
                                    const shouldHideClearSectionButton =
                                        items.every(item => item.configItem?.group?.type === 'single') ||
                                        search.length ||
                                        !isExpanded;
                                    const components = items.map(({ configItem, index, fields }) =>
                                        _getComponent(configItem, index, fields)
                                    );

                                    if (!components.find(component => !!component)) return null;

                                    return (
                                        <div
                                            key={section}
                                            className={classNames(css.fieldGroupHolder, {
                                                [css.fieldGroupHolderCollapsed]: !isExpanded,
                                            })}
                                            style={style}
                                            onClick={() => !isExpanded && toggleSectionExpansion(section, isExpanded)}
                                        >
                                            {label && (
                                                <div
                                                    className={
                                                        isTimeBreakdown
                                                            ? css.fieldGroupTitle
                                                            : css.fieldGroupTitleClickable
                                                    }
                                                    onClick={() =>
                                                        isExpanded &&
                                                        !isTimeBreakdown &&
                                                        toggleSectionExpansion(section, isExpanded)
                                                    }
                                                >
                                                    <div
                                                        className={classNames(
                                                            css.fieldGroupTitle,
                                                            !isTimeBreakdown && css.fullWidth
                                                        )}
                                                    >
                                                        {icon && <Icon name={icon} />}
                                                        <div className={!isTimeBreakdown && css.fieldGroupContent}>
                                                            <Translate id={label} />
                                                            {NEW_SECTIONS.has(section) && (
                                                                <Tag
                                                                    shouldTranslate
                                                                    color="var(--pink100)"
                                                                    value="STATIC.PAGES.REPORTS.NEW_TAG"
                                                                />
                                                            )}
                                                            {!shouldHideClearSectionButton && (
                                                                <ClearFieldsButton
                                                                    reportType={reportType}
                                                                    section={label}
                                                                    items={items}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    {!isTimeBreakdown && !search.length && (
                                                        <Icon name={isExpanded ? 'up-fill' : 'down-fill'} />
                                                    )}
                                                </div>
                                            )}
                                            {shouldShowCreativeInterpolationWarning(section) &&  (
                                                    <WarningMessage
                                                        className={css.sectionSubtitle}
                                                        message="STATIC.PAGES.REPORTS.CREATIVE_INTERPOLATION_MESSAGE"
                                                        type="info"
                                                    />
                                                )}
                                            {isExpanded &&
                                                components.map((component, index) =>
                                                    component ? (
                                                        <Fragment key={`${items[index].configItem.type}_${index}`}>
                                                            {component}
                                                        </Fragment>
                                                    ) : null
                                                )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    ))}
                {shouldShowEmptyState && (
                    <div className={css.shelfContainer} style={{ marginTop: '30px' }}>
                        <ReportsEmptyState reportType={reportType} />
                    </div>
                )}
                {showSpinner && <Spinner show />}
                {!isEmptyStateMode && !shouldShowNoData && !showSpinner && table.show && (
                    <div className={css.dataContainer}>
                        {isChartEnabled && (
                            <TransitionGroup>
                                {!showSpinner && table.show && (
                                    <CSSTransition key="divider" {...TRANSITION_CONFIG}>
                                        <div
                                            className={css.hideElementDivider}
                                            style={{
                                                marginBottom: '20px',
                                                marginTop: '20px',
                                            }}
                                        >
                                            <span onClick={handleToggleChart}>
                                                <ChartIcon className={css.collapsedIcon} />
                                                <div className={css.collapsedText}>
                                                    <Translate
                                                        id={`STATIC.BUTTONS.${
                                                            chart.show ? 'HIDE_CHART' : 'SHOW_CHART'
                                                        }`}
                                                    />
                                                </div>
                                                <ChevronIcon
                                                    className={classNames(css.chevronIcon, {
                                                        [`${css.expanded}`]: chart.show,
                                                        [`${css.collapsed}`]: !chart.show,
                                                    })}
                                                />
                                            </span>
                                        </div>
                                    </CSSTransition>
                                )}
                                {chart.show && (
                                    <CSSTransition key="chart" {...TRANSITION_CONFIG}>
                                        <LineChart
                                            spinnerStyle={{ zIndex: 1 }}
                                            numOfTableRows={reportData.rowData ? reportData.rowData.length : 0}
                                            chartData={chartData}
                                            timeBreakdown={timeBreakdown}
                                            chart={chart}
                                            onLinesChanged={onChartLinesChanged}
                                            onChartLegendToggle={onChartLegendToggle}
                                            onMetricSelected={onChartMetricSelected}
                                            onChartLoaded={onChartLoaded}
                                            startDate={table.request?.start_date || ''}
                                            endDate={table.request?.end_date || ''}
                                            chartTimeBreakdown={chart.chartTimeBreakdown || null}
                                            onTimeBreakdownChanged={onChartTimeBreakdownChanged}
                                            onMouseEnter={() => {
                                                hoverStartTime = new Date();
                                            }}
                                            onMouseLeave={() => {
                                                const hoverTime = new Date() - hoverStartTime;
                                                if (hoverTime > 5000) {
                                                    trackMixpanelEvent(REPORTS_EVENT_PREFIX, 'Hover on Chart', {
                                                        page: reportType,
                                                        hoverTime,
                                                    });
                                                }
                                            }}
                                        />
                                    </CSSTransition>
                                )}
                            </TransitionGroup>
                        )}
                        {showPivotCharts &&
                            table.show &&
                            isPivotReport(reportType) &&
                            reportData.reportId &&
                            reportData.rowData?.length && (
                                <PivotChart
                                    reportId={reportData.reportId}
                                    rowData={reportData.rowData}
                                    reportColumns={reportData.columnDefs.map(x => ({
                                        name: x.field,
                                        fieldType: x.type,
                                        displayName: x.headerName,
                                        displayFormat: x.context?.displayFormat,
                                    }))}
                                    showControls={adminMode}
                                    settings={chart}
                                    onSettingsChange={chartSettings => {
                                        onPivotChartChange(chartSettings);
                                        onUpdateQueryURL();
                                    }}
                                />
                            )}
                        {table.show && (
                            <>
                                {showReportMeta && reportData && (
                                    <WarningMessage
                                        type="info"
                                        showIcon={false}
                                        show
                                        message={`report_id: ${reportData.reportId}. Report creation time: ${reportData.cacheTimestamp}`}
                                        containerStyle={{
                                            marginTop: '50px',
                                        }}
                                    />
                                )}
                                {showReportMeta && reportData && (
                                    <WarningMessage
                                        type="info"
                                        showIcon={false}
                                        show
                                        message={`Org using Calendric Cohort: ${reportData.useCalendricCohort}`}
                                        className={css.innerInfoMessages}
                                    />
                                )}
                                {showReportMeta && reportData && tiDpouMigrationDatesMessage && (
                                    <WarningMessage
                                        type="info"
                                        showIcon={false}
                                        show
                                        message={tiDpouMigrationDatesMessage}
                                        className={css.innerInfoMessages}
                                    />
                                )}
                                {reportData && reportData.customer && (
                                    <WarningMessage
                                        type="info"
                                        showIcon={false}
                                        show
                                        message="STATIC.PAGES.REPORTS.PARTNER_TIMEZONE_INFO"
                                        textReplace={{
                                            customer: reportData.customer,
                                            attributionTimezone: reportData.attributionTimezone,
                                        }}
                                        containerStyle={{
                                            marginTop: '50px',
                                            marginBottom: '10px',
                                        }}
                                    />
                                )}
                                <Grid
                                    id={`report_${reportType}`}
                                    key={`report_${reportType}`}
                                    reportType={reportType}
                                    {...reportData}
                                    pivotMode={isPivotMode(reportType, pivotTable || {})}
                                    pivotTable={pivotTable}
                                    pinnedColumns={pinnedColumns}
                                    className={chart.show ? css.gridContainer : css.gridContainerWhenNoChart}
                                />
                            </>
                        )}
                    </div>
                )}
                {shouldShowNoData && !shouldShowEmptyState && <ReportsNoData />}
                <Shelf
                    open={openFieldsAvailability}
                    shelfSize="medium"
                    headerText="STATIC.PAGES.FIELDS_AVAILABILITY_SHELF.HEADER_TEXT"
                    HeaderImage={GlassesIcon}
                    headerImageClassName={css.fieldsAvailabilityShelfIcon}
                    closeOnClickOutside
                    onClose={closeFieldsAvailabilityShelf}
                >
                    <FieldsAvailability
                        availabilityParameter={AVAILABILITY_PARAMETER_OPTION__DIMENSION.name}
                        fieldName={fieldsAvailabilityFieldName}
                        onClose={closeFieldsAvailabilityShelf}
                        reportType={reportType}
                    />
                </Shelf>
                <EditShelf open={isEditShelfOpen} reportType={reportType} />
            </PageWrapper>
        </>
    );
};

Reports.propTypes = {
    shelf: PropTypes.arrayOf(PropTypes.any),
    onFieldClick: PropTypes.func,
    onFieldSet: PropTypes.func,
    onChartLinesChanged: PropTypes.func,
    onChartLegendToggle: PropTypes.func,
    onChartMetricSelected: PropTypes.func,
    onChartLoaded: PropTypes.func,
    reportType: PropTypes.string,
    showFieldsSelection: PropTypes.bool,
    showSpinner: PropTypes.bool,
    header: PropTypes.objectOf(PropTypes.any),
    chartData: PropTypes.objectOf(PropTypes.any),
    reportData: PropTypes.objectOf(PropTypes.any),
    chart: PropTypes.objectOf(PropTypes.any),
    table: PropTypes.objectOf(PropTypes.any),
    validationError: PropTypes.string,
    onToggleChart: PropTypes.func,
    showReportMeta: PropTypes.bool,
    onPivotChartChange: PropTypes.func,
    onUpdateQueryURL: PropTypes.func,
    permissionKeys: PropTypes.arrayOf(PropTypes.string),
    isSlimMode: PropTypes.bool,
    customFieldGroupLabels: PropTypes.objectOf(PropTypes.any),
    fieldGroupsToHide: PropTypes.objectOf(PropTypes.any),
    onFieldsAvailabilityClick: PropTypes.func,
    showFieldsAvailabilityFeature: PropTypes.bool,
    adminMode: PropTypes.bool,
    selectedSources: PropTypes.arrayOf(PropTypes.any),
    selectedFields: PropTypes.objectOf(PropTypes.any),
    reportWarning: PropTypes.shape({
        text: PropTypes.string,
        params: PropTypes.objectOf(PropTypes.any),
    }),
    changePopupDialog: PropTypes.func,
    showPopupDialog: PropTypes.bool,
    popupDialogParams: PropTypes.objectOf(PropTypes.any),
    emptyStateOrgData: PropTypes.objectOf(PropTypes.any),
    onCloseReportsEmptyState: PropTypes.func,
    isEditShelfOpen: PropTypes.bool,
    isShelfReporting: PropTypes.bool,
    onFiltersExpand: PropTypes.func,
    user: PropTypes.objectOf(User),
    reportFilters: PropTypes.arrayOf(
        PropTypes.shape({
            field: PropTypes.string,
            operator: PropTypes.string,
            tags: PropTypes.arrayOf(PropTypes.string),
        })
    ),
    translate: PropTypes.func,
    onSearchFilterChanged: PropTypes.func,
    onUpdateChartData: PropTypes.func,
    selectedApps: PropTypes.arrayOf(PropTypes.any),
};

export default Reports;
